import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import { useProperty } from '../context/PropertyContext';
// Removed unused imports from react-bootstrap
import 'bootstrap/dist/css/bootstrap.min.css';
// import AlertNotifications from '../components/AlertNotifications'; // Currently unused
import DesktopLayout from '../layouts/DesktopLayout';
import PropertySwitcher from '../components/common/PropertySwitcher';
import ErrorBoundary from '../components/ErrorBoundary';
import { getDashboardTitle } from '../config/navigation';
import { toLocalIso } from '../utils/dateValueHelper';
import { BUSINESS_TIMEZONE } from '../config/timezoneConfig';
import { formatCompactDate, formatDateTime } from '../utils/dateFormatter';
import { getPlatformDisplayName } from '../utils/platformUtils';
import axios from 'axios';
import secureApiService, { secureApi } from '../services/secureApi';
import moment from 'moment-timezone';
import 'moment/locale/ar';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import '../styles/AdminDashboard.css';
import OwnerStatCards from '../components/owner/OwnerStatCards';
import useLanguage from '../hooks/useLanguage';
import OwnerPlatformBreakdown from '../components/owner/OwnerPlatformBreakdown';
import OwnerMonthlyTrend from '../components/owner/OwnerMonthlyTrend';

// Configure Moment.js for Arabic with Riyadh timezone
moment.locale('ar');

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { t } = useLanguage();
  const { selectedProperty, setSelectedProperty } = useProperty();
  const currentDate = new Date(); // Used for calendar initialDate only

  // States for existing functionality
  const [properties, setProperties] = useState([]);
  const [, setConflictAlert] = useState({ show: false, message: "" }); // Used in checkForPropertyConflicts

  // Dashboard states for property owners
  const emptyStats = {
    totalRevenue: 0,
    totalBookings: 0,
    occupancyRate: 0,
    averageStayDuration: 0,
    platformBreakdown: {},
    monthlyTrend: [],
    revenuePerNight: 0
  };
  const [dashboardStats, setDashboardStats] = useState(emptyStats);
  const [periodInProgress, setPeriodInProgress] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [activeNavItem, setActiveNavItem] = useState("overview");

  // Date range filter states
  const [dateRangeType, setDateRangeType] = useState('6months'); // '3months', '6months', 'custom'
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  // Only the تطبيق button copies the pickers into this — metrics never fetch
  // from half-edited input state
  const [appliedCustomRange, setAppliedCustomRange] = useState({ from: null, to: null });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [metricsError, setMetricsError] = useState(false);
  // Until the first successful load there are no figures at all — the zero placeholders must never
  // be shown as if they were the owner's revenue (tasks/owner-screens-mobile.md step 7).
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [metricsReload, setMetricsReload] = useState(0);

  // Alert states (disabled - functionality moved to AdminDashboard)
  const [showAlertDropdown, setShowAlertDropdown] = useState(false);
  const [unreadAlertCount] = useState(0);
  const [alerts] = useState([]);
  const [recentAlerts] = useState([]);
  const markAllAlertsAsRead = () => {}; // No-op
  const markAlertAsRead = () => {}; // No-op

  // Platform color mapping for the calendar
  const platformColors = {
    'Airbnb': '#ff5a5f',
    'Booking.com': '#003580',
    'أخرى': '#4caf50'
  };

  // Shown above the stat cards when a metrics fetch fails — last good stats
  // stay on screen rather than misleading zeros
  const metricsErrorBanner = metricsError ? (
    <div dir="rtl" style={{
      background: '#fff3cd',
      border: '1px solid #ffc107',
      color: '#664d03',
      padding: '10px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px'
    }}>
      <i className="bi bi-exclamation-triangle" style={{ marginInlineEnd: '6px' }}></i>
      تعذر تحديث الإحصائيات — تعرض آخر بيانات متاحة
      <button type="button" className="btn btn-link btn-sm p-0" style={{ marginInlineStart: '8px' }}
        onClick={() => setMetricsReload(n => n + 1)}>
        {t('states.retry')}
      </button>
    </div>
  ) : null;

  // ===== Metrics via the backend revenue engine (single source of truth) =====
  // All stats come from /api/v1/revenue — the same corrected engine as the
  // /finance page (Riyadh business-day boundaries, night-clipping, proration).
  // No client-side stat math here (see tasks/fix-finance-dashboard-metrics.md).

  // The selected range as bare YYYY-MM-DD bounds (server resolves them as
  // full Riyadh business days). Returns null while a custom range has not
  // been applied so we never fire a half-formed request.
  const getSelectedRange = () => {
    if (dateRangeType === 'custom') {
      return appliedCustomRange.from && appliedCustomRange.to
        ? { startDate: appliedCustomRange.from, endDate: appliedCustomRange.to }
        : null;
    }
    const monthsBack = dateRangeType === '3months' ? 3 : 6;
    const from = new Date();
    const dayOfMonth = from.getDate();
    from.setMonth(from.getMonth() - monthsBack);
    if (from.getDate() !== dayOfMonth) from.setDate(0); // clamp 29-31 rollover to month end
    return { startDate: toLocalIso(from), endDate: toLocalIso(new Date()) };
  };

  const mapMetricsToStats = (metrics) => {
    const currentMonthKey = toLocalIso(new Date()).slice(0, 7);
    return {
      totalRevenue: metrics.revenue?.gross || 0,
      revenuePerNight: metrics.rates?.adr || 0,
      occupancyRate: metrics.occupancy?.rate || 0,
      totalBookings: metrics.bookings?.total || 0,
      averageStayDuration: metrics.bookings?.averageStayLength || 0,
      platformBreakdown: (metrics.byPlatform || []).reduce((acc, p) => {
        acc[getPlatformDisplayName(p.platform)] = p.bookings;
        return acc;
      }, {}),
      monthlyTrend: (metrics.byMonth || []).map(m => ({
        month: m.month,
        bookings: m.bookings,
        revenue: m.grossRevenue,
        isUpcoming: m.month > currentMonthKey
      }))
    };
  };
// Authentication check
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      console.warn('No token found, redirecting to login');
      navigate('/login');
      return;
    }

    // Admin redirect now handled by RoleGuard at router level (AppSecure.js line 118)
  }, [navigate, user]);

  // Store raw data for filtering
  const [allProperties, setAllProperties] = useState([]);

  // Fetch properties (calendar, conflicts, property views) on initial load
useEffect(() => {
  const fetchDashboardData = async () => {
    try {
      // Fetch properties using secure API
      let propertiesData = [];
      try {
        propertiesData = await secureApiService.getProperties();
        setAllProperties(propertiesData); // Store all properties
        setProperties(propertiesData);

      } catch (err) {
        console.error("Error fetching properties:", err);
        if (err.response?.status === 403) {
          console.error("Access denied to properties");
        }
        setAllProperties([]);
        setProperties([]);
      }

      // Generate calendar events from properties
      generateCalendarEvents(propertiesData);

      // Check for conflicts in loaded properties
      checkForPropertyConflicts(propertiesData);

      // Alert fetching removed - property owners don't need it
      // Admins are redirected to AdminDashboard
    } catch (error) {
      // Top-level error handling
      console.error("❌ Error fetching dashboard data:", error);

      // Reset states in case of a top-level error
      setAllProperties([]);
      setProperties([]);
    }
  };

  fetchDashboardData();

  // Set up the hourly refresh
  const refreshInterval = setInterval(() => {
    fetchDashboardData();
  }, 60 * 60 * 1000); // Every hour

  return () => clearInterval(refreshInterval); // Clean up on unmount
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [user]);

// Validate selectedProperty exists in properties array (prevent stale selection)
useEffect(() => {
  if (selectedProperty && allProperties.length > 0) {
    const exists = allProperties.some(p => String(p._id) === String(selectedProperty));
    if (!exists) {
      setSelectedProperty(null); // Reset to "All Properties"
    }
  }
}, [allProperties, selectedProperty, setSelectedProperty]);

// Keep the visible property list in sync with the switcher
useEffect(() => {
  if (!allProperties.length) return;
  setProperties(
    selectedProperty
      ? allProperties.filter(p => String(p._id) === String(selectedProperty))
      : allProperties
  );
}, [selectedProperty, allProperties]);

// Fetch stats from the backend revenue engine whenever the selection or
// applied range changes (and hourly, to match the properties refresh)
useEffect(() => {
  if (!user) return;
  let cancelled = false; // stale responses must never overwrite newer state

  const fetchMetrics = async () => {
    // Recomputed each run so the hourly tick follows the calendar day
    const range = getSelectedRange();
    if (!range) return; // custom range not applied yet — keep current stats

    try {
      const params = { period: 'custom', ...range };
      const url = selectedProperty
        ? `/api/v1/revenue/${selectedProperty}/metrics`
        : '/api/v1/revenue/portfolio/metrics';
      const { data } = await secureApi.get(url, { params });
      if (cancelled) return;

      if (data?.success && data.metrics) {
        setDashboardStats(mapMetricsToStats(data.metrics));
        // Window still open → the card says the nights are elapsed-only (backend-derived).
        setPeriodInProgress(!!data.dateRange?.inProgress);
      } else {
        setDashboardStats(emptyStats); // e.g. portfolio with no properties
        setPeriodInProgress(false);
      }
      setMetricsError(false);
      setStatsLoaded(true);
    } catch (error) {
      if (cancelled) return;
      console.error('❌ Error fetching revenue metrics:', error);
      // Keep the last good stats on screen; the banner says they're stale —
      // zeros would read as "no revenue"
      setMetricsError(true);
    }
  };

  fetchMetrics();
  const refreshInterval = setInterval(fetchMetrics, 60 * 60 * 1000);
  return () => {
    cancelled = true;
    clearInterval(refreshInterval);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user, selectedProperty, dateRangeType, appliedCustomRange, metricsReload]);


// Generate calendar events from properties (FullCalendar format)
  const generateCalendarEvents = (propertiesData) => {
    const allEvents = [];

    propertiesData.forEach(property => {
      if (!property.bookings || !Array.isArray(property.bookings)) return;

      property.bookings.forEach(booking => {
        if (!booking.startDate || !booking.endDate) return;

        const startDate = new Date(booking.startDate);
        const endDate = new Date(booking.endDate);

        // Add a day to end date for proper display (calendar is non-inclusive of end date)
        const displayEndDate = new Date(endDate);
        displayEndDate.setDate(displayEndDate.getDate() + 1);

        const source = booking.source || 'أخرى';
        const hasConflict = checkForConflict(property.bookings, booking);
        const backgroundColor = platformColors[source] || platformColors['أخرى'];

        allEvents.push({
          id: `${property._id}-${startDate}-${endDate}`,
          title: `${property.name} (${source})`,
          start: startDate,
          end: displayEndDate,
          backgroundColor: backgroundColor,
          borderColor: hasConflict ? '#ff0000' : backgroundColor,
          extendedProps: {
            propertyId: property._id,
            propertyName: property.name,
            source: source,
            hasConflict: hasConflict
          }
        });
      });
    });

    setCalendarEvents(allEvents);
  };

  // Check for conflicts in loaded properties
  const checkForPropertyConflicts = (propertiesData) => {
    if (propertiesData && propertiesData.length > 0) {
      for (const property of propertiesData) {
        if (property.bookings && property.bookings.length > 0) {
          let foundConflict = false;
          
          // Check each booking for conflicts
          for (const booking of property.bookings) {
            const isConflict = property.bookings.some(otherBooking => {
              // Skip comparing with itself
              if (booking === otherBooking) return false;
              
              // Skip if they're from the same source
              if (booking.platform === otherBooking.platform) return false;
              
              // Check for date overlap
              const bookingStart = new Date(booking.start);
              const bookingEnd = new Date(booking.end);
              const otherStart = new Date(otherBooking.start);
              const otherEnd = new Date(otherBooking.end);
              
              return bookingStart < otherEnd && bookingEnd > otherStart;
            });
            
            if (isConflict) {
              foundConflict = true;
              break;
            }
          }
          
          // If conflict found, show alert
          if (foundConflict) {
            setConflictAlert({
              show: true,
              message: `⚠️ تم اكتشاف تعارضات في الحجوزات لـ "${property.name}"`
            });
            
            // Conflict alert removed - Channex handles conflicts automatically
            
            // Exit after finding first property with conflicts
            break;
          }
        }
      }
    }
  };

// Check if a booking conflicts with others
  const checkForConflict = (allBookings, currentBooking) => {
    if (!currentBooking || !currentBooking.startDate || !currentBooking.endDate) return false;
    
    const currentStart = new Date(currentBooking.startDate);
    const currentEnd = new Date(currentBooking.endDate);
    
    return allBookings.some(otherBooking => {
      // Skip comparing with itself
      if (otherBooking === currentBooking) return false;
      
      // Skip if they're from the same source (likely duplicates)
      if (otherBooking.source === currentBooking.source) return false;
      
      if (!otherBooking || !otherBooking.startDate || !otherBooking.endDate) return false;
      
      const otherStart = new Date(otherBooking.startDate);
      const otherEnd = new Date(otherBooking.endDate);
      
      // Check for overlap
      return currentStart < otherEnd && currentEnd > otherStart;
    });
  };

  // Define alert types as constants for consistency
  const ALERT_TYPES = {
    NEW_BOOKING: 'new_booking',
    BOOKING_CONFLICT: 'booking_conflict',
    SYNC_FAILURE: 'sync_failure'
  };

  // Alert functions removed - not needed for property owners

  // Logout handler
  const handleLogout = () => {
    logout(); // Use AuthContext's logout
    navigate('/login');
  };

  // Function to refresh all properties (reusing existing function)
  const refreshAllProperties = async () => {
    try {
      await axios.post('/api/sync/manual');

      // Refresh the properties data using secure API
      const data = await secureApiService.getProperties();
      setProperties(data);

      // Regenerate calendar events
      generateCalendarEvents(data);
    } catch (error) {
      console.error('Error during refresh:', error);
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', { 
      style: 'currency', 
      currency: 'SAR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Function to render properties view (extracted for reuse in admin tab)
  const renderPropertiesView = () => {
    return (
      <>
        {/* This will show the existing properties grid */}
        <p>العقارات المتاحة</p>
      </>
    );
  };

  // Unified return statement with conditional content
  return (
    <>
      {/* Admin and TurnKey Owner view with DesktopLayout */}
      {(user?.role === 'admin' || user?.role === 'turnKeyOwner') ? (
        <DesktopLayout
          activeNavItem={activeNavItem}
          setActiveNavItem={setActiveNavItem}
          headerTitle={getDashboardTitle(user?.role)}
        >
          {/* Main Dashboard Content */}
            {/* Overview Tab */}
            {activeNavItem === 'overview' && (
              <div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <h3 style={{ margin: 0, color: '#471396', fontSize: 'clamp(20px, 4vw, 24px)' }}>
                    {user?.role === 'admin' ? 'نظرة عامة على النظام' : 'نظرة عامة على العقارات'}
                  </h3>
                  {user?.role === 'turnKeyOwner' && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <ErrorBoundary fallback={<div style={{ color: '#666', fontSize: '14px' }}>خطأ في تحميل محدد العقارات</div>}>
                        <PropertySwitcher properties={allProperties} />
                      </ErrorBoundary>

                      {/* Date Range Filter */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            setDateRangeType('3months');
                            setShowCustomDatePicker(false);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: dateRangeType === '3months' ? '#471396' : 'white',
                            color: dateRangeType === '3months' ? 'white' : '#471396',
                            border: `2px solid ${dateRangeType === '3months' ? '#471396' : '#ddd'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: dateRangeType === '3months' ? '600' : '400',
                            transition: 'all 0.2s',
                            minHeight: '44px'
                          }}
                        >
                          آخر 3 أشهر
                        </button>

                        <button
                          onClick={() => {
                            setDateRangeType('6months');
                            setShowCustomDatePicker(false);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: dateRangeType === '6months' ? '#471396' : 'white',
                            color: dateRangeType === '6months' ? 'white' : '#471396',
                            border: `2px solid ${dateRangeType === '6months' ? '#471396' : '#ddd'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: dateRangeType === '6months' ? '600' : '400',
                            transition: 'all 0.2s',
                            minHeight: '44px'
                          }}
                        >
                          آخر 6 أشهر
                        </button>

                        <button
                          onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: dateRangeType === 'custom' ? '#FFC600' : 'white',
                            color: dateRangeType === 'custom' ? '#333' : '#666',
                            border: `2px solid ${dateRangeType === 'custom' ? '#FFC600' : '#ddd'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: dateRangeType === 'custom' ? '600' : '400',
                            transition: 'all 0.2s',
                            minHeight: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <i className="bi bi-calendar-range"></i>
                          اختر فترة مخصصة
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Date Range Picker */}
                {user?.role === 'turnKeyOwner' && showCustomDatePicker && (() => {
                  // Calculate min and max dates for the date picker (last 12 months)
                  const today = new Date();
                  const twelveMonthsAgo = new Date();
                  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

                  const minDate = twelveMonthsAgo.toISOString().split('T')[0];
                  const maxDate = today.toISOString().split('T')[0];

                  return (
                    <div style={{
                      backgroundColor: 'white',
                      border: '2px solid #FFC600',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '16px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#471396', fontSize: '16px' }}>
                        اختر الفترة المخصصة
                        <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginRight: '8px' }}>
                          (البيانات المتاحة: آخر 12 شهر)
                        </span>
                      </h4>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'end', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 200px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontSize: '14px', fontWeight: '500' }}>
                            من تاريخ:
                          </label>
                          <input
                            type="date"
                            value={customDateRange.from || ''}
                            min={minDate}
                            max={maxDate}
                            onChange={(e) => setCustomDateRange({ ...customDateRange, from: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '2px solid #ddd',
                              borderRadius: '8px',
                              fontSize: '14px',
                              minHeight: '44px'
                            }}
                          />
                        </div>

                        <div style={{ flex: '1 1 200px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontSize: '14px', fontWeight: '500' }}>
                            إلى تاريخ:
                          </label>
                          <input
                            type="date"
                            value={customDateRange.to || ''}
                            min={minDate}
                            max={maxDate}
                            onChange={(e) => setCustomDateRange({ ...customDateRange, to: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '2px solid #ddd',
                              borderRadius: '8px',
                              fontSize: '14px',
                              minHeight: '44px'
                            }}
                          />
                        </div>

                        <button
                          onClick={() => {
                            if (!customDateRange.from || !customDateRange.to) {
                              alert('الرجاء اختيار تاريخ البداية والنهاية');
                              return;
                            }

                            // Validate that "from" date is before or equal to "to" date
                            if (customDateRange.from > customDateRange.to) {
                              alert('تاريخ البداية يجب أن يكون قبل أو يساوي تاريخ النهاية');
                              return;
                            }

                            // The old 12-months/no-future cap is gone — the backend
                            // engine serves any range, incl. upcoming bookings

                            setAppliedCustomRange({ ...customDateRange });
                            setDateRangeType('custom');
                            setShowCustomDatePicker(false);
                          }}
                          style={{
                            padding: '10px 24px',
                            backgroundColor: '#471396',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            minHeight: '44px',
                            flex: '0 0 auto'
                          }}
                        >
                          تطبيق
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Admin users are redirected to AdminDashboard - no admin UI needed here */}

                {/* TurnKey Owner Simplified Dashboard (v2.0) */}
                {user?.role === 'turnKeyOwner' && (
                  <div className="owner-dashboard">
                    {!statsLoaded ? (
                      metricsError ? (
                        <div className="alert alert-danger" dir="rtl" role="alert">
                          {t('ownerDashboard.statsLoadError')}
                          <button type="button" className="btn btn-outline-danger btn-sm touch-btn"
                            style={{ marginInlineStart: '12px' }}
                            onClick={() => setMetricsReload(n => n + 1)}>
                            {t('states.retry')}
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-5" dir="rtl">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('states.loading')}</span>
                          </div>
                          <p className="mt-2 text-muted">{t('states.loading')}</p>
                        </div>
                      )
                    ) : (
                      <>
                        {metricsErrorBanner}
                        <OwnerStatCards dashboardStats={dashboardStats} periodInProgress={periodInProgress} />

                        <OwnerPlatformBreakdown
                          platformBreakdown={dashboardStats.platformBreakdown}
                          totalBookings={dashboardStats.totalBookings}
                        />

                        <OwnerMonthlyTrend monthlyTrend={dashboardStats.monthlyTrend} />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Properties Tab */}
            {activeNavItem === 'properties' && (
              <div>
                <h3 style={{ marginBottom: '25px', color: '#471396' }}>إدارة العقارات</h3>
                {renderPropertiesView()}
              </div>
            )}

        </DesktopLayout>
      ) : (
        /* DIY Owner view with DesktopLayout */
        <DesktopLayout
          activeNavItem={activeNavItem}
          setActiveNavItem={setActiveNavItem}
          headerTitle={getDashboardTitle(user?.role)}
        >
          {/* DIY Owner Dashboard Content */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "20px"
      }}>
        <h2 style={{ margin: 0 }}>  لوحة التحكم الرئيسية   </h2>
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* Alert Icon and Dropdown */}
          <div style={{ position: "relative", marginLeft: "15px" }}>
            <button 
              onClick={() => setShowAlertDropdown(!showAlertDropdown)}
              style={{
                position: "relative",
                padding: "8px 10px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "22px"
              }}
            >
              🔔
              {unreadAlertCount > 0 && (
                <span style={{
                  position: "absolute",
                  top: "0",
                  right: "0",
                  background: "#ff5a5f",
                  color: "white",
                  borderRadius: "50%",
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold"
                }}>
                  {unreadAlertCount > 9 ? '9+' : unreadAlertCount}
                </span>
              )}
            </button>

            {/* Alerts disabled - not needed for owners */}
            {false && showAlertDropdown && (
              <div style={{
                position: "absolute",
                top: "45px",
                right: "-50px",
                width: "420px",
                maxHeight: "500px",
                overflowY: "auto",
                background: "#fff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                borderRadius: "8px",
                zIndex: 1000,
                direction: "rtl",
                overflow: "visible",
                maxWidth: "99vw",
                transform: "translateX(60px)", 
                textAlign: "right"
              }}>
                {/* Header with link to alerts dashboard */}
                <div style={{
                  padding: "16px",
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "sticky",
                  top: 0,
                  background: "#fff",
                  zIndex: 1
                }}>
                  <h3 style={{ margin: 0 }}>التنبيهات</h3>
                  <div>
                    <Link 
                      to="/alerts"
                      style={{
                        marginRight: "15px",
                        color: "#2196f3",
                        textDecoration: "none",
                        fontSize: "14px"
                      }}
                    >
                      إدارة التنبيهات
                    </Link>
                    
                    {unreadAlertCount > 0 && (
                      <button 
                        onClick={markAllAlertsAsRead}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#4caf50",
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "8px"
                        }}
                      >
                        تعليم الكل كمقروءة
                      </button>
                    )}
                  </div>
                </div>
                
                {alerts.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                    لا توجد تنبيهات جديدة
                  </div>
                ) : (
                  <div>
                    {alerts.map(alert => (
                      <div 
                        key={alert.id} 
                        onClick={() => markAlertAsRead(alert.id)}
                        style={{
                          padding: "12px 15px",
                          borderBottom: "1px solid #f5f5f5",
                          cursor: "pointer",
                          backgroundColor: alert.read ? "#fff" : "#f8f8f8",
                          transition: "background-color 0.2s"
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = alert.read ? "#fff" : "#f8f8f8"}
                      >
                        <div style={{ display: "flex", alignItems: "flex-start" }}>
                          <div style={{ marginLeft: "10px", fontSize: "20px" }}>
                            {alert.type === ALERT_TYPES.NEW_BOOKING && "🆕"}
                            {alert.type === ALERT_TYPES.BOOKING_CONFLICT && "⚠️"}
                            {alert.type === ALERT_TYPES.SYNC_FAILURE && "❌"}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: alert.read ? "normal" : "bold" }}>
                              {alert.message}
                            </div>
                            <div style={{ 
                              marginTop: "5px", 
                              fontSize: "12px", 
                              color: "#888",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}>
                              <span>
                                {formatDateTime(alert.timestamp)}
                              </span>
                              <span style={{
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                backgroundColor: 
                                  alert.type === ALERT_TYPES.NEW_BOOKING ? "#e8f5e9" : 
                                  alert.type === ALERT_TYPES.BOOKING_CONFLICT ? "#ffebee" : 
                                  "#fff3e0",
                                color: 
                                  alert.type === ALERT_TYPES.NEW_BOOKING ? "#2e7d32" : 
                                  alert.type === ALERT_TYPES.BOOKING_CONFLICT ? "#c62828" : 
                                  "#ef6c00"
                              }}>
                                {alert.type === ALERT_TYPES.NEW_BOOKING && "حجز جديد"}
                                {alert.type === ALERT_TYPES.BOOKING_CONFLICT && "تعارض"}
                                {alert.type === ALERT_TYPES.SYNC_FAILURE && "خطأ في المزامنة"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button 
            onClick={handleLogout}
            style={{
              padding: "8px 15px",
              backgroundColor: "#f0f0f0",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            🚪 تسجيل الخروج
          </button>
        </div>
      </div>

{/* Main Dashboard Layout with Sidebar */}
<div style={{ 
  display: "flex", 
  flexDirection: "row",
  gap: "20px"
}}>
        {/* Right Sidebar Navigation Menu */}
        <div style={{ 
          width: "250px",
          backgroundColor: "#f9f9f9",
          borderRadius: "8px",
          padding: "20px",
          height: "fit-content"
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: "20px",
            borderBottom: "1px solid #eee",
            paddingBottom: "10px" 
          }}>
            القائمة الرئيسية
          </h3>
          
          {/* Sidebar Navigation */}
          <nav>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {/* Dashboard Link */}
              <li style={{ marginBottom: "15px" }}>
                <Link 
                  to="/dashboard"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: activeNavItem === "dashboard" ? "#4caf50" : "#333",
                    backgroundColor: activeNavItem === "dashboard" ? "#ebf7eb" : "transparent",
                    borderRadius: "4px",
                    fontWeight: activeNavItem === "dashboard" ? "bold" : "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    if (activeNavItem !== "dashboard") {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeNavItem !== "dashboard") {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={() => setActiveNavItem("dashboard")}
                >
                  <span style={{ marginLeft: "10px", fontSize: "18px" }}>🏠</span>
                  <span>لوحة التحكم الرئيسية</span>
                </Link>
              </li>


              {/* Properties Link */}
              <li style={{ marginBottom: "15px" }}>
                <Link 
                  to="/properties" 
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: activeNavItem === "properties" ? "#4caf50" : "#333",
                    backgroundColor: activeNavItem === "properties" ? "#ebf7eb" : "transparent",
                    borderRadius: "4px",
                    fontWeight: activeNavItem === "properties" ? "bold" : "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    if (activeNavItem !== "properties") {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeNavItem !== "properties") {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={() => setActiveNavItem("properties")}
                >
                  <span style={{ marginLeft: "10px", fontSize: "18px" }}>🏡</span>
                  <span>إدارة العقارات</span>
                </Link>
              </li>

              {/* Bookings Link */}
              <li style={{ marginBottom: "15px" }}>
                <Link 
                  to="/bookings" 
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: activeNavItem === "bookings" ? "#4caf50" : "#333",
                    backgroundColor: activeNavItem === "bookings" ? "#ebf7eb" : "transparent",
                    borderRadius: "4px",
                    fontWeight: activeNavItem === "bookings" ? "bold" : "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    if (activeNavItem !== "bookings") {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeNavItem !== "bookings") {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={() => setActiveNavItem("bookings")}
                >
                  <span style={{ marginLeft: "10px", fontSize: "18px" }}>📖</span>
                  <span>إدارة الحجوزات</span>
                </Link>
              </li>

              {/* Financial Management Link - NEW */}
              <li style={{ marginBottom: "15px" }}>
                <Link 
                  to="/finance" 
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: activeNavItem === "financial" ? "#4caf50" : "#333",
                    backgroundColor: activeNavItem === "financial" ? "#ebf7eb" : "transparent",
                    borderRadius: "4px",
                    fontWeight: activeNavItem === "financial" ? "bold" : "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    if (activeNavItem !== "financial") {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeNavItem !== "financial") {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={() => setActiveNavItem("financial")}
                >
                  <span style={{ marginLeft: "10px", fontSize: "18px" }}>💰</span>
                  <span>الإدارة المالية</span>
                </Link>
              </li>

              {/* Alerts Link */}
              <li style={{ marginBottom: "15px" }}>
                <Link 
                  to="/alerts" 
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: activeNavItem === "alerts" ? "#4caf50" : "#333",
                    backgroundColor: activeNavItem === "alerts" ? "#ebf7eb" : "transparent",
                    borderRadius: "4px",
                    fontWeight: activeNavItem === "alerts" ? "bold" : "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    if (activeNavItem !== "alerts") {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeNavItem !== "alerts") {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={() => setActiveNavItem("alerts")}
                >
                  <span style={{ marginLeft: "10px", fontSize: "18px" }}>⚠️</span>
                  <span>التنبيهات</span>
                </Link>
              </li>

              {/* Settings Link */}
              <li>
                <Link 
                  to="/settings" 
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: activeNavItem === "settings" ? "#4caf50" : "#333",
                    backgroundColor: activeNavItem === "settings" ? "#ebf7eb" : "transparent",
                    borderRadius: "4px",
                    fontWeight: activeNavItem === "settings" ? "bold" : "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    if (activeNavItem !== "settings") {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeNavItem !== "settings") {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={() => setActiveNavItem("settings")}
                >
                  <span style={{ marginLeft: "10px", fontSize: "18px" }}>⚙️</span>
                  <span>إعدادات الحساب</span>
                </Link>
              </li>
            </ul>
          </nav>
          
          {/* Refresh Button */}
          <div style={{ 
            marginTop: "30px",
            textAlign: "center"
          }}>
            <button 
              onClick={refreshAllProperties}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4caf50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                width: "100%",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px"
              }}
            >
              <span>🔄</span>
              <span>تحديث البيانات</span>
            </button>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div style={{ flex: 1 }}>
          {/* Quick Stats Section */}
          <div style={{ 
            marginBottom: "25px"
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: "15px",
              borderBottom: "1px solid #eee",
              paddingBottom: "10px"
            }}>
              إحصائيات سريعة
            </h3>

            {metricsErrorBanner}

            <div style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap"
            }}>
              {/* Revenue Card */}
              <div style={{ 
                flex: "1 1 250px",
                backgroundColor: "#e3f2fd",
                borderRadius: "8px",
                padding: "20px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                borderRight: "5px solid #2196f3"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <h4 style={{ margin: 0 }}>إجمالي الإيرادات</h4>
                  <span style={{ fontSize: "24px" }}>💰</span>
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#2196f3" }}>
                  {formatCurrency(dashboardStats.totalRevenue)}
                </div>
                <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                  خلال الفترة المحددة
                </div>
              </div>
              
              {/* Bookings Card */}
              <div style={{ 
                flex: "1 1 250px",
                backgroundColor: "#fff8e1",
                borderRadius: "8px",
                padding: "20px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                borderRight: "5px solid #ff9800"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <h4 style={{ margin: 0 }}>إجمالي الحجوزات</h4>
                  <span style={{ fontSize: "24px" }}>📑</span>
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#ff9800" }}>
                  {dashboardStats.totalBookings}
                </div>
                <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                  جميع الحجوزات النشطة
                </div>
              </div>
              
              {/* Occupancy Card */}
              <div style={{ 
                flex: "1 1 250px",
                backgroundColor: "#e8f5e9",
                borderRadius: "8px",
                padding: "20px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                borderRight: "5px solid #4caf50"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <h4 style={{ margin: 0 }}>معدل الإشغال</h4>
                  <span style={{ fontSize: "24px" }}>📈</span>
                </div>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#4caf50" }}>
                  {dashboardStats.occupancyRate}%
                </div>
                <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                  متوسط الإشغال لجميع العقارات
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Section */}
          <div style={{ marginBottom: "25px" }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "15px",
              borderBottom: "1px solid #eee",
              paddingBottom: "10px"
            }}>
              <h3 style={{ margin: 0 }}>نظرة عامة على الحجوزات</h3>
              <Link 
                to="/bookings" 
                style={{
                  textDecoration: "none",
                  color: "#2196f3",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "14px"
                }}
              >
                <span>إدارة الحجوزات</span>
                <span>↗️</span>
              </Link>
            </div>
            
            {/* Mini Calendar */}
            <div style={{
              height: "300px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #eee",
              padding: "10px",
              direction: "rtl"
            }}>
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin, momentTimezonePlugin]}
                initialView="dayGridMonth"
                timeZone={BUSINESS_TIMEZONE}
                locale="ar"
                direction="rtl"
                initialDate={currentDate}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: ''
                }}
                buttonText={{
                  today: 'اليوم',
                  month: 'شهر',
                  prev: 'السابق',
                  next: 'التالي'
                }}
                events={calendarEvents}
                eventContent={(eventInfo) => {
                  const hasConflict = eventInfo.event.extendedProps?.hasConflict;
                  return (
                    <div style={{
                      backgroundColor: eventInfo.event.backgroundColor || '#471396',
                      color: 'white',
                      padding: '2px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      border: hasConflict ? '3px solid #ff0000' : 'none'
                    }}>
                      {eventInfo.event.title}
                    </div>
                  );
                }}
                height="100%"
                dayMaxEvents={2}
                moreLinkText={(num) => `+ ${num}`}
              />
            </div>
          </div>
          
{/* Recent Alerts Section */}
          <div>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "15px",
              borderBottom: "1px solid #eee",
              paddingBottom: "10px"
            }}>
              <h3 style={{ margin: 0 }}>آخر التنبيهات</h3>
              <Link 
                to="/alerts" 
                style={{
                  textDecoration: "none",
                  color: "#2196f3",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "14px"
                }}
              >
                <span>عرض جميع التنبيهات</span>
                <span>↗️</span>
              </Link>
            </div>
            
            {/* Alerts List with Improved Error Handling */}
            <div>
              {!Array.isArray(recentAlerts) || recentAlerts.length === 0 ? (
                <div style={{ 
                  backgroundColor: "#f9f9f9", 
                  padding: "20px", 
                  borderRadius: "8px",
                  textAlign: "center",
                  color: "#666"
                }}>
                  لا توجد تنبيهات جديدة
                </div>
              ) : (
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  gap: "10px" 
                }}>
                  {recentAlerts.map((alert, index) => (
                    <div 
                      key={alert._id || alert.id || `alert-${index}`} 
  style={{
    padding: "15px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    borderRight: alert.type === 'booking_conflict' 
      ? "5px solid #f44336"   // Red for conflict alerts
      : alert.type === 'sync_failure' 
        ? "5px solid #ff9800"  // Orange for sync failures
        : alert.type === 'new_booking'
          ? "5px solid #4caf50"  // Green for new bookings
          : "5px solid #2196f3"  // Blue for other types
                      }}
                    >
                      <div style={{ 
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "10px"
                      }}>
                        <span style={{ fontSize: "20px" }}>
                          {alert.type === 'booking_conflict' && "⚠️"}
                          {alert.type === 'sync_failure' && "❌"}
                          {alert.type === 'new_booking' && "🆕"}
                        </span>
                        <div style={{ 
                          flex: 1,
                          fontWeight: !alert.read ? "bold" : "normal" 
                        }}>
                          {alert.message || 'تنبيه جديد'}
                        </div>
                        <div style={{ 
                          fontSize: "12px",
                          color: "#888"
                        }}>
                          {alert.createdAt ? formatCompactDate(alert.createdAt) : '-'}
                        </div>
                      </div>
                      
                      {alert.propertyId && (
                        <div style={{ 
                          fontSize: "13px",
                          color: "#666",
                          marginTop: "5px" 
                        }}>
                          العقار: {properties.find(p => p._id === alert.propertyId)?.name || 'غير معروف'}
                        </div>
                      )}
                      
                      {!alert.resolved && (
                        <div style={{ 
                          marginTop: "10px",
                          textAlign: "left" 
                        }}>
                          <Link 
                            to={`/alerts/${alert._id || alert.id || index}`}
                            style={{
                              display: "inline-block",
                              padding: "5px 10px",
                              backgroundColor: "#f5f5f5",
                              borderRadius: "4px",
                              textDecoration: "none",
                              color: "#333",
                              fontSize: "13px"
                            }}
                          >
                            حل المشكلة
                          </Link>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
        </DesktopLayout>
      )}
    </>
  );
};

export default OwnerDashboard;
